import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../utils/api';
import { useAuth, ROLES } from './AuthContext';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const calculateCartTotals = (items) => {
  const hasPromo = items.length > 0 && items.some((i) => i.promo_sp_per_unit != null);

  const priceTotal = items.reduce((sum, item) => {
    const sp = hasPromo && item.promo_sp_per_unit != null ? item.promo_sp_per_unit : (item.sp_per_unit_discount || item.price);
    return sum + sp * item.quantity;
  }, 0);

  const totalGst = items.reduce((sum, item) => {
    const gst = hasPromo && item.promo_gst_per_unit != null ? item.promo_gst_per_unit : (item.gst_after_discount ?? 0);
    return sum + gst * item.quantity;
  }, 0);

  const grandTotal = items.reduce((sum, item) => {
    const total = hasPromo && item.promo_final_per_unit != null ? item.promo_final_per_unit : (item.final_sp_unit_discount ?? item.price);
    return sum + total * item.quantity;
  }, 0);

  const originalGrandTotal = items.reduce((sum, item) => sum + ((item.final_sp_unit_discount ?? item.price) * item.quantity), 0);
  const promoSaving = hasPromo ? originalGrandTotal - grandTotal : 0;

  return { priceTotal, totalGst, grandTotal, originalGrandTotal, promoSaving, hasPromo };
};

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const normalizeErrorMessage = (payload, fallbackMessage) => {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (Array.isArray(payload)) {
    const messages = payload
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry.msg === 'string') return entry.msg;
        if (entry && typeof entry.message === 'string') return entry.message;
        return null;
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join(', ') : fallbackMessage;
  }

  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
    if (typeof payload.msg === 'string' && payload.msg.trim()) return payload.msg;
    if (typeof payload.code === 'string' && payload.code === 'phone_verification_required') {
      return 'Please verify your phone on WhatsApp before completing purchase.';
    }
    if (typeof payload.code === 'string' && payload.code === 'email_verification_required') {
      return 'Please verify your email before completing purchase.';
    }
  }

  return fallbackMessage;
};

export const CartProvider = ({ children }) => {
  const { user, hasRole, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutErrorCode, setCheckoutErrorCode] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState('');
  const isBuyer = hasRole(ROLES.BUYER);

  useEffect(() => {
    const loadCart = async () => {
      if (authLoading) return;

      if (!isBuyer) {
        setItems([]);
        return;
      }

      try {
        const response = await axios.get('/api/cart');
        setItems(response.data || []);
      } catch {
        setItems([]);
      }
    };

    loadCart();
  }, [authLoading, isBuyer, user?.id]);

  useEffect(() => {
    // Hide previous payment success once cart has active items again.
    if (items.length > 0 && checkoutSuccess) {
      setCheckoutSuccess('');
    }
  }, [items.length, checkoutSuccess]);

  const addItem = async (product, variant, quantity = 1) => {
    if (!isBuyer) {
      return { success: false, error: 'Only Buyer role can add items to cart.' };
    }
    if (quantity < 1) {
      return { success: false, error: 'Quantity must be at least 1.' };
    }

    const max_stock = variant?.current_quantity ?? variant?.stock_quantity ?? 0;
    if (quantity > max_stock) {
      return { success: false, error: 'Quantity exceeds available stock.' };
    }

    const itemKey = `${product.id}-${variant?.id || 'none'}`;
    const existing = items.find((item) => item.key === itemKey);
    const existingQty = Number(existing?.quantity || 0);
    if ((existingQty + quantity) > max_stock) {
      return { success: false, error: 'Requested quantity plus existing cart quantity exceeds available stock.' };
    }

    try {
      const response = await axios.post('/api/cart/items', {
        product_id: product.id,
        variant_id: variant?.id || null,
        quantity,
      });
      const updatedItem = response.data;
      setItems((prev) => {
        const exists = prev.some((i) => i.key === updatedItem.key);
        if (exists) {
          return prev.map((i) => (i.key === updatedItem.key ? updatedItem : i));
        }
        return [...prev, updatedItem];
      });
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: typeof detail === 'string' ? detail : 'Could not add item to cart. Please try again.',
      };
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`/api/cart/items/${itemId}`);
      setItems((prev) => prev.filter((item) => item.id !== itemId));
    } catch (error) {
      // If the backend already removed the item (for example, after an unpaid
      // order was created), treat the delete as successful and clear the stale row.
      if (error.response?.status === 404) {
        setItems((prev) => prev.filter((item) => item.id !== itemId));
      }
    }
  };

  const updateItemQuantity = async (itemId, quantity) => {
    if (quantity < 1) return;

    const targetItem = items.find((item) => item.id === itemId);
    if (!targetItem) return;
    if (quantity > (targetItem.max_stock || 0)) return;

    try {
      const response = await axios.put(`/api/cart/items/${itemId}`, { quantity });
      const updated = response.data;
      setItems((prev) => prev.map((item) => (item.id === itemId ? updated : item)));
    } catch {
      // Keep UI unchanged on failure.
    }
  };

  const clearCart = async () => {
    if (!isBuyer) {
      setItems([]);
      return;
    }
    try {
      await axios.delete('/api/cart/clear');
    } finally {
      setItems([]);
    }
  };

  const applyPromo = async (code) => {
    try {
      const response = await axios.post('/api/cart/apply-promo', { promo_code: code });
      setItems(response.data || []);
      return { success: true };
    } catch (err) {
      const detail = err.response?.data?.detail;
      return { success: false, error: typeof detail === 'string' ? detail : 'Failed to apply promo code' };
    }
  };

  const removePromo = async () => {
    try {
      const response = await axios.delete('/api/cart/promo');
      setItems(response.data || []);
    } catch {
      // Silently refresh cart on failure.
      try {
        const response = await axios.get('/api/cart');
        setItems(response.data || []);
      } catch {}
    }
  };

  const getCartTotal = () => calculateCartTotals(items).grandTotal;

  const checkout = async (shippingAddressId = null) => {
    if (items.length === 0) {
      setCheckoutError('Cart is empty');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError('');
    setCheckoutErrorCode('');
    setCheckoutSuccess('');

    const payload = {
      currency: items[0]?.currency || 'INR',
      shipping_address_id: shippingAddressId,
      items: items.map((item) => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
      })),
      promo_code: items[0]?.applied_promo_code || null,
    };

    try {
      const scriptReady = await loadRazorpayScript();
      if (!scriptReady) {
        throw new Error('Failed to load Razorpay checkout. Please try again.');
      }

      const createPaymentResponse = await axios.post('/api/payments/create-order', payload);
      const paymentData = createPaymentResponse.data;

      // The order is now created server-side; remove items from cart so the buyer
      // does not accidentally create a second unpaid order from the cart.
      await clearCart();

      await new Promise((resolve, reject) => {
        // Track whether payment was completed so ondismiss knows not to reject.
        let paymentCompleted = false;

        const options = {
          key: paymentData.key_id,
          amount: paymentData.amount,
          currency: paymentData.currency,
          name: paymentData.name,
          description: paymentData.description,
          order_id: paymentData.razorpay_order_id,
          prefill: {
            name: paymentData.prefill_name,
            email: paymentData.prefill_email,
          },
          handler: async (razorpayResponse) => {
            try {
              await axios.post('/api/payments/verify', {
                order_id: paymentData.order_id,
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
              });
              paymentCompleted = true;
              resolve();
            } catch (verifyErr) {
              reject(verifyErr);
            }
          },
          modal: {
            // Only treat dismissal as cancellation when no successful payment occurred.
            // If the user retried after a failure and it succeeded, handler already resolved.
            ondismiss: () => {
              if (!paymentCompleted) {
                reject(new Error('Payment was cancelled.'));
              }
            },
          },
          theme: {
            color: '#0d6efd',
          },
        };

        const razorpay = new window.Razorpay(options);

        // Do NOT reject the Promise on payment.failed — Razorpay keeps the modal
        // open so the user can retry. Rejecting here would settle the Promise before
        // the retry's success handler can resolve it, causing the "cart not cleared"
        // / stale error bug when a retry succeeds in the same session.
        razorpay.on('payment.failed', () => {
          // Intentionally left empty; Razorpay displays its own failure UI with retry option.
        });

        razorpay.open();
      });

      setCheckoutSuccess('Payment successful. Order ID: ' + paymentData.order_id);
      await clearCart();
    } catch (err) {
      const responseData = err.response?.data;
      const detail = responseData?.detail;
      const errorCode = detail && typeof detail === 'object' ? detail.code : '';
      setCheckoutErrorCode(typeof errorCode === 'string' ? errorCode : '');
      if (errorCode === 'unpaid_order_exists') {
        await clearCart();
      }
      const errorMsg = normalizeErrorMessage(detail ?? responseData?.message ?? err.message, 'Checkout failed');
      setCheckoutError(errorMsg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const value = {
    items,
    addItem,
    removeItem,
    updateItemQuantity,
    clearCart,
    applyPromo,
    removePromo,
    getCartTotal,
    checkout,
    checkoutLoading,
    checkoutError,
    checkoutErrorCode,
    checkoutSuccess,
    calculateCartTotals,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};